import { PublicLayoutContent } from '@components/layout/public';
import { IS_DEV } from '@constants/common';
import { CommonEWFormFields } from '@extendedWarranty/components/CommonEWFormFields';
import { EWFileUpload } from '@extendedWarranty/components/EWFileUpload';
import { FeedbackDialog } from '@extendedWarranty/components/FeedbackDialog';
import { ManualUpload } from '@extendedWarranty/components/ManualUpload';
import { ExtendedWarrantyPageHeader } from '@extendedWarranty/components/PageHeader';
import { useExtendedWarrantyContext } from '@extendedWarranty/contexts/ExtendedWarrantyContext';
import { usePrefixedNavigate } from '@hooks/usePrefixedNavigate';
import CheckBox from '@mui/icons-material/CheckBox';
import Close from '@mui/icons-material/Close';
import UploadFileOutlined from '@mui/icons-material/UploadFileOutlined';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

export const UploadSerialNumbers = () => {
    const [open, setOpen] = useState(false);
    const handleFeedbackOpen = () => {
        setOpen(true);
    };

    const {
        setLockDateField,
        setLockNameField,
        dropzoneState,
        isProcessingFiles,
        isValidateSerialNumbersLoading,
        formik: { values, resetForm, handleSubmit, submitForm, setFieldValue, isValid },
    } = useExtendedWarrantyContext();

    const navigate = usePrefixedNavigate();
    const location = useLocation();

    const isNewInstallation = useMemo(() => !location.state, [location]);

    const pageTitle = useMemo(() => {
        if (values.uploadByFile) {
            return 'Extended warranty upload';
        } else {
            if (isNewInstallation) {
                return 'Register new system for Extended warranty';
            } else {
                return 'Add additional devices to existing system with extended warranty';
            }
        }
    }, [values.uploadByFile, isNewInstallation]);

    const handleCancel = useCallback(() => {
        resetForm();
        navigate('/extended-warranty');
    }, [resetForm, navigate]);

    useEffect(() => {
        if (location.state?.installation_name) {
            setLockNameField(true);
            setFieldValue('installation_name', location.state?.installation_name);
        }
        if (location.state?.date_installed) {
            setLockDateField(true);
            setFieldValue('date_installed', location.state?.date_installed);
        }
        if (location.state?.id) {
            setFieldValue('id', location.state?.id);
        }
    }, [setLockDateField, setLockNameField, values, location, setFieldValue]);

    return (
        <PublicLayoutContent
            containerProps={{
                className: clsx('relative', {
                    '!overflow-hidden': open,
                }),
            }}
        >
            <Box
                sx={{ color: (theme) => theme.palette['neutral-900'].main }}
                className="flex flex-col font-inter m-auto gap-8 pt-12 pb-32 md:pb-9 md:pt-28 px-4 relative"
            >
                <ExtendedWarrantyPageHeader
                    feedbackEnabled
                    title={pageTitle}
                    subtitle={
                        values.uploadByFile ? (
                            <>
                                To activate extended warranty in bulk by uploading a file, please{' '}
                                <Typography
                                    display="inline"
                                    color="primary-600.main"
                                    className="cursor-pointer"
                                    component="a"
                                    href={`${IS_DEV ? '' : '/static'}/extended_warranty_template.xlsx`}
                                    download
                                >
                                    download
                                </Typography>{' '}
                                and use the example file (xls/xlsx).
                            </>
                        ) : (
                            'To activate the extended warranty provide the serial numbers and installation details below'
                        )
                    }
                    handleModalOpen={handleFeedbackOpen}
                />
                <div className="flex flex-col bg-white w-full p-4 md:p-11 gap-14">
                    <form id="upload-serials-form" onSubmit={handleSubmit} className="flex flex-col gap-7 h-full">
                        {!values.uploadByFile && <CommonEWFormFields />}
                        {values.uploadByFile ? <EWFileUpload /> : <ManualUpload />}
                    </form>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                        <div
                            className={clsx('hidden md:flex', {
                                '!hidden': !values.uploadByFile,
                            })}
                        >
                            <Button
                                className="rounded-sm text-base"
                                disableElevation={false}
                                size="large"
                                startIcon={<UploadFileOutlined />}
                                onClick={() => dropzoneState?.inputRef?.current?.click()}
                            >
                                <span className="text-nowrap">CHOOSE FILES</span>
                            </Button>
                        </div>
                        <div className="flex flex-col md:flex-row w-full flex-grow justify-between md:justify-end gap-3">
                            <Button
                                color="neutral-650"
                                disableElevation={false}
                                size="large"
                                startIcon={<Close />}
                                className="rounded-sm text-base"
                                onClick={handleCancel}
                            >
                                CANCEL
                            </Button>
                            <Button
                                disabled={!isValid}
                                disableElevation={false}
                                size="large"
                                startIcon={
                                    !isValidateSerialNumbersLoading && !isProcessingFiles ? (
                                        <CheckBox />
                                    ) : (
                                        <CircularProgress color="white" size={24} />
                                    )
                                }
                                className="rounded-sm text-base"
                                type="submit"
                                onClick={submitForm}
                            >
                                <span
                                    className={clsx({
                                        'ml-1': isValidateSerialNumbersLoading,
                                    })}
                                >
                                    PROCESS
                                </span>
                            </Button>
                        </div>
                    </div>
                </div>
                <Button
                    color="white"
                    disableElevation={false}
                    onClick={() => setFieldValue('uploadByFile', !values.uploadByFile)}
                    startIcon={values.uploadByFile ? <FormatAlignLeftIcon /> : <UploadFileOutlined />}
                    size="large"
                    className="self-end ml-auto rounded-sm"
                >
                    <span>
                        {values.uploadByFile
                            ? 'ENTER SERIAL NUMBERS MANUALLY'
                            : 'UPLOAD SERIAL NUMBERS WITH A FILE (XLS/XLSX)'}
                    </span>
                </Button>
            </Box>
            <FeedbackDialog open={open} setOpen={setOpen} />
        </PublicLayoutContent>
    );
};
